import { c as _c } from "react/compiler-runtime";
import { useContext } from 'react';
import styled, { css, useTheme } from 'styled-components';
import LINKS from '../../../../configs/links';
import Badge from '../../../common/base/Badge';
import { ClickableArea } from '../../../common/base/Button';
import { StyledCard, StyledCardSection } from '../../../common/base/Card';
import Container from '../../../common/base/Container';
import Icon, { IconName } from '../../../common/base/Icon';
import Link from '../../../common/base/Link';
import Text from '../../../common/base/Text';
import { Div, StyledUtilsProps } from '../../../common/helpers/StyledUtils';
import { DashboardContext } from '../DashboardContext';
import { StyledViewLayout, StyledViewContent } from '../../../layouts/ViewLayout';
const Grid = styled(Div)<{
  columns?: number;
  gapX?: string;
  gapY?: string;
  marginTop?: string;
}>(({
  columns,
  gapX,
  gapY,
  marginTop
}) => css`
    display: grid;
    grid-template-columns: repeat(${columns || 2}, minmax(0, 1fr));
    gap: 32px;
    ${gapX && `column-gap: ${gapX};`}
    ${gapY && `row-gap: ${gapY};`}
    margin-top: ${marginTop || '24px'};

    @media (max-width: 800px) {
      grid-template-columns: repeat(1, minmax(0, 1fr));
    }
  `);
interface GetStartedCardProps {
  heading?: React.ReactNode;
  text?: React.ReactNode;
  icon: IconName;
  onClick?: () => void;
  to?: string;
  href?: string;
  completed?: boolean;
  variant?: 'step' | 'docs';
}
const StyledCardWrapper = styled(StyledCard)`
  overflow: hidden;
  border: none;
  box-shadow: ${({
  theme
}) => theme.shadows.neutral.base};
`;
const StyledIconWrapper = styled.div`
  display: none;

  ${StyledCardWrapper}:hover & {
    display: contents;
  }
  * {
    cursor: pointer;
  }
`;
const Dot = styled(Div)<{
  muted?: boolean;
  danger?: boolean;
  success?: boolean;
  primary?: boolean;
  warning?: boolean;
  dark?: boolean;
  px?: number;
}>(({
  muted,
  danger,
  primary,
  warning,
  dark,
  success,
  px,
  theme
}) => css`
    width: ${px || 8}px;
    height: ${px || 8}px;
    border-radius: 50%;
    ${muted && css`
      background-color: ${theme.colors.on.neutral.secondary_neutral};
    `};

    ${dark && css`
      background-color: ${theme.colors.on.neutral.primary_neutral};
    `};

    ${danger && css`
      background-color: ${theme.colors.on.neutral.danger};
    `};

    ${primary && css`
      background-color: ${theme.colors.on.neutral.primary};
    `};

    ${warning && css`
      background-color: ${theme.colors.on.neutral.warning};
    `};

    ${success && css`
      background-color: ${theme.colors.on.neutral.success};
    `};
  `);
const GetStartedCard = t0 => {
  const $ = _c(34);
  const {
    variant: t1,
    heading: t2,
    text: t3,
    icon,
    onClick,
    to,
    href,
    completed: t4
  } = t0;
  const variant = t1 === undefined ? "docs" : t1;
  const heading = t2 === undefined ? "" : t2;
  const text = t3 === undefined ? "" : t3;
  const completed = t4 === undefined ? false : t4;
  let t5;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      height: "100%"
    };
    $[0] = t5;
  } else {
    t5 = $[0];
  }
  const t6 = href && Link;
  const t7 = href && "_blank";
  let t8;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      justify: "space-between",
      align: "center"
    };
    $[1] = t8;
  } else {
    t8 = $[1];
  }
  const t9 = !completed;
  let t10;
  let t11;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {
      px: 32
    };
    t11 = {
      px: 32
    };
    $[2] = t10;
    $[3] = t11;
  } else {
    t10 = $[2];
    t11 = $[3];
  }
  let t12;
  if ($[4] !== completed || $[5] !== icon || $[6] !== t9) {
    t12 = <Badge subtle={t9} neutral={completed} h={t10} w={t11} icon={icon} />;
    $[4] = completed;
    $[5] = icon;
    $[6] = t9;
    $[7] = t12;
  } else {
    t12 = $[7];
  }
  let t13;
  if ($[8] !== completed || $[9] !== variant) {
    t13 = completed ? <Div flex={{
      align: "center",
      gap: 1
    }}><Icon muted={true} icon="check" /><Text muted={true} semi={true}>Done</Text></Div> : variant === "docs" ? <StyledIconWrapper><Icon icon="link" muted={true} large={true} /></StyledIconWrapper> : <Div flex={{
      align: "center",
      gap: 2
    }}><Dot primary={true} /><Text muted={true} semi={true}>To-do</Text></Div>;
    $[8] = completed;
    $[9] = variant;
    $[10] = t13;
  } else {
    t13 = $[10];
  }
  let t14;
  if ($[11] !== t12 || $[12] !== t13) {
    t14 = <Div flex={t8}>{t12}{t13}</Div>;
    $[11] = t12;
    $[12] = t13;
    $[13] = t14;
  } else {
    t14 = $[13];
  }
  let t15;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = {
      t: 8
    };
    $[14] = t15;
  } else {
    t15 = $[14];
  }
  let t16;
  if ($[15] !== completed || $[16] !== variant) {
    t16 = variant === "step" && !completed && <StyledIconWrapper><Icon icon="arrow_forward" /></StyledIconWrapper>;
    $[15] = completed;
    $[16] = variant;
    $[17] = t16;
  } else {
    t16 = $[17];
  }
  let t17;
  if ($[18] !== completed || $[19] !== heading || $[20] !== t16) {
    t17 = <Text bold={true} muted={completed} text_wrap={true} m={t15}>{heading}{" "}{t16}</Text>;
    $[18] = completed;
    $[19] = heading;
    $[20] = t16;
    $[21] = t17;
  } else {
    t17 = $[21];
  }
  let t18;
  if ($[22] !== text) {
    t18 = <Text muted={true} text_wrap={true}>{text}</Text>;
    $[22] = text;
    $[23] = t18;
  } else {
    t18 = $[23];
  }
  let t19;
  if ($[24] !== completed || $[25] !== href || $[26] !== onClick || $[27] !== t14 || $[28] !== t17 || $[29] !== t18 || $[30] !== t6 || $[31] !== t7 || $[32] !== to) {
    t19 = <StyledCardWrapper raised={1}><StyledCardSection h={100}><ClickableArea h={100} style={t5} as={t6} highlight={completed} disabled={completed} to={to} href={href} target={t7} onClick={onClick} no_underline={true} p={4}>{t14}{t17}{t18}</ClickableArea></StyledCardSection></StyledCardWrapper>;
    $[24] = completed;
    $[25] = href;
    $[26] = onClick;
    $[27] = t14;
    $[28] = t17;
    $[29] = t18;
    $[30] = t6;
    $[31] = t7;
    $[32] = to;
    $[33] = t19;
  } else {
    t19 = $[33];
  }
  return t19;
};
const StyledImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
`;
const StyledImageWrapper = styled.div<StyledUtilsProps>(({
  theme
}) => css`
    border-radius: ${theme.radius.normal};
    overflow: hidden;
    border: ${theme.border};
    padding: 0;
    margin-bottom: 24px;
    line-height: 0;
  `);
const UseCase = t0 => {
  const $ = _c(16);
  const {
    img,
    heading,
    text,
    href
  } = t0;
  let t1;
  if ($[0] !== heading || $[1] !== img) {
    t1 = <StyledImageWrapper><StyledImg src={img} alt={heading} /></StyledImageWrapper>;
    $[0] = heading;
    $[1] = img;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== heading) {
    t2 = <Text size="l" bold={true}>{heading}</Text>;
    $[3] = heading;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      b: 3,
      t: 2
    };
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== text) {
    t4 = <Text m={t3} muted={true}>{text}</Text>;
    $[6] = text;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <Icon style={{
      textDecoration: "none"
    }} icon="link" pointer={true} />;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== href) {
    t6 = <Link href={href} target="_blank">Quickstart {t5}</Link>;
    $[9] = href;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== t1 || $[12] !== t2 || $[13] !== t4 || $[14] !== t6) {
    t7 = <Div>{t1}{t2}{t4}{t6}</Div>;
    $[11] = t1;
    $[12] = t2;
    $[13] = t4;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  return t7;
};
const GetStartedView: React.FC = () => {
  const $ = _c(32);
  const {
    has_connection,
    has_created_connections,
    has_team_members_or_invites
  } = useContext(DashboardContext);
  const theme = useTheme();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      x: 14
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      t: 10,
      b: 22
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <Div flex={{
      align: "center",
      gap: 4
    }}><Text as="h1" size="2xl" bold={true}>Get Started</Text></Div>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const t3 = has_created_connections ? "/connections/new" : "/create-first-connection";
  let t4;
  if ($[3] !== has_connection || $[4] !== t3) {
    t4 = <GetStartedCard variant="step" heading="Create a connection" icon="connections" text="Create your first connection to begin routing events through Hookdeck." to={t3} completed={has_connection} />;
    $[3] = has_connection;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== has_team_members_or_invites) {
    t5 = <GetStartedCard variant="step" heading="Invite team members" icon="group" text="Invite team members to your organization for a free 14-day trial period." to="/settings/organization/members?tab=invited" completed={has_team_members_or_invites} />;
    $[6] = has_team_members_or_invites;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== t4 || $[9] !== t5) {
    t6 = <Grid>{t4}{t5}</Grid>;
    $[8] = t4;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <Div m={{
      t: 18
    }}><Text size="xl" bold={true}>Hookdeck use cases</Text></Div>;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const t8 = `/images/receive-events-${theme.mode}.png`;
  let t9;
  if ($[12] !== t8) {
    t9 = <UseCase img={t8} heading="Receive events" text="Use Hookdeck as your inbound webhook infrastructure." href="https://hookdeck.com/docs/receive-webhooks" />;
    $[12] = t8;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  const t10 = `/images/send-events-${theme.mode}.png`;
  let t11;
  if ($[14] !== t10) {
    t11 = <UseCase img={t10} heading="Send events" text="Use Hookdeck as your outbound webhook infrastructure." href="https://hookdeck.com/docs/send-webhooks" />;
    $[14] = t10;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  const t12 = `/images/third-party-${theme.mode}.png`;
  let t13;
  if ($[16] !== t12) {
    t13 = <UseCase img={t12} heading="Third party event routing" text="Use Hookdeck to integrate third-party APIs and services." href="https://hookdeck.com/docs/third-party-routing" />;
    $[16] = t12;
    $[17] = t13;
  } else {
    t13 = $[17];
  }
  const t14 = `/images/async-api-${theme.mode}.png`;
  let t15;
  if ($[18] !== t14) {
    t15 = <UseCase img={t14} heading="Asynchronous API" text="Use Hookdeck as your asynchronous API infrastructure." href="https://hookdeck.com/docs/asynchronous-apis" />;
    $[18] = t14;
    $[19] = t15;
  } else {
    t15 = $[19];
  }
  let t16;
  if ($[20] !== t11 || $[21] !== t13 || $[22] !== t15 || $[23] !== t9) {
    t16 = <Grid gapY="56px">{t9}{t11}{t13}{t15}</Grid>;
    $[20] = t11;
    $[21] = t13;
    $[22] = t15;
    $[23] = t9;
    $[24] = t16;
  } else {
    t16 = $[24];
  }
  let t17;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <Div m={{
      t: 18
    }}><Text size="xl" bold={true}>Hookdeck basics</Text></Div>;
    $[25] = t17;
  } else {
    t17 = $[25];
  }
  let t18;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <Grid columns={3}><GetStartedCard heading="Connections" icon="connections" text="Connections route events from a request source to an event destination." href={LINKS.product_docs.connections} /><GetStartedCard heading="Sources" icon="source" text="A source represents a service that sends HTTP requests to Hoodkeck." href={LINKS.product_docs.sources} /><GetStartedCard heading="Destinations" icon="destinations" text="Destinations represent endpoints your events can be delivered to." href={LINKS.product_docs.destinations} /><GetStartedCard heading="Requests" icon="requests" text="Requests are HTTP requests that Hookdeck receives from a source." href={LINKS.product_docs.requests} /><GetStartedCard heading="Events & Attempts" icon="events" text="Events are outgoing requests from Hookdeck. Attempts are specific event delivery efforts." href={LINKS.product_docs.events} /><GetStartedCard heading="Issues & Notifications" icon="inbox" text="Track problems and communicate resolutions with your team." href={LINKS.product_docs.issues} /></Grid>;
    $[26] = t18;
  } else {
    t18 = $[26];
  }
  let t19;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <Div m={{
      t: 18
    }}><Text size="xl" bold={true}>Learn more</Text></Div>;
    $[27] = t19;
  } else {
    t19 = $[27];
  }
  let t20;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <Grid columns={3}><GetStartedCard heading="Transformations" icon="transformation" text="Transformations let you modify events prior to delivery." href={LINKS.product_docs.transformations} /><GetStartedCard heading="Filters" icon="filter" text="Conditionally allow and route events based on their contents." href={LINKS.product_docs.filters} /><GetStartedCard heading="Retries" icon="retry" text="Manually or automatically retry previous event deliveries." href={LINKS.product_docs.retries} /></Grid>;
    $[28] = t20;
  } else {
    t20 = $[28];
  }
  let t21;
  if ($[29] !== t16 || $[30] !== t6) {
    t21 = <StyledViewLayout><StyledViewContent><Div p={t0}><Container xxlarge={true} h={100} p={t1}>{t2}{t6}{t7}{t16}{t17}{t18}{t19}{t20}</Container></Div></StyledViewContent></StyledViewLayout>;
    $[29] = t16;
    $[30] = t6;
    $[31] = t21;
  } else {
    t21 = $[31];
  }
  return t21;
};
export default GetStartedView;